/**
 * The four batch states and their locked presentation.
 *
 * Design: design-context/latest/2026-08-20-batch-management/
 *   design/Locked Decisions.md ("The words", "Status treatment").
 *
 * The words and glosses are LOCKED -- do not reword them here. The gloss is
 * what makes "Open" mean *collecting* rather than *unresolved", and what makes
 * "Holding" read as a task rather than an error.
 *
 * `tone` is exposed in the DOM as `data-tone` so the color rules are
 * assertable without a computed-style primitive (the ui-runner has none, and
 * a class-name assertion would just be testing Vuetify internals). The rules
 * it encodes:
 *
 *   - amber belongs to Holding and NOTHING else;
 *   - red belongs to Failed and to destructive actions and nothing else;
 *   - Released is inert, so its tone is the neutral slate.
 */

export const BATCH_STATUS = Object.freeze({
  open: Object.freeze({
    label: "Open",
    gloss: "Collecting orders",
    tone: "blue",
    icon: "radio_button_checked",
  }),
  holding: Object.freeze({
    label: "Holding",
    gloss: "Waiting on you",
    tone: "amber",
    // Deliberately NOT an error icon: "Holding never uses an error icon."
    icon: "rate_review",
  }),
  released: Object.freeze({
    label: "Released",
    gloss: "Sent to the decorator",
    tone: "slate",
    icon: "lock",
  }),
  failed: Object.freeze({
    label: "Failed",
    gloss: "Needs attention",
    tone: "red",
    icon: "error_outline",
  }),
  // The drain is RUNNING. `batches.state` is literally "releasing"
  // (batch_release.py:240, RELEASING_STATE), set by the CAS that starts the
  // release and cleared when the drain finishes.
  //
  // Blue, not amber: nothing is waiting on the distributor. Amber is locked
  // to Holding ("Waiting on you") and this is the opposite -- the system is
  // working and the correct action is to watch, not to act. Giving it amber
  // would put two different meanings on one colour on the same screen.
  //
  // It needs its own entry for the same reason `release_blocked` does:
  // `statusMeta` is keyed on the BACKEND state and defaults to `open`, so
  // without this key a batch mid-drain renders a healthy "Collecting orders"
  // chip -- which is both wrong and reassuring, the worst combination.
  releasing: Object.freeze({
    label: "Releasing",
    gloss: "Sending to the decorator",
    tone: "blue",
    icon: "sync",
  }),

  // A release STOPPED before the close because money had been collected for
  // orders the decorator refused (backend: `batch_release`'s close gate).
  //
  // NOT a fifth colour. Red, like Failed, because the reader's question is
  // the same one -- "is something wrong here that I have to deal with?" --
  // and the answer is yes, more urgently than for an ordinary send failure:
  // a card has been charged for goods that are not in production. The gloss
  // is what separates the two.
  //
  // It needs its own entry rather than reusing `failed` because `statusMeta`
  // is keyed on the BACKEND state, and `batches.state` is literally
  // "release_blocked". Without this key it falls through to the `open`
  // default and renders a healthy blue "Collecting orders" chip on a batch
  // with stranded money in it.
  release_blocked: Object.freeze({
    label: "Blocked",
    // Neutral by default: whether anything was CHARGED or SENT is a fact
    // about the batch's per-order outcome, not about its state, and the
    // outcome is only read when the row is opened. `statusMeta` swaps in the
    // specific wording once told (#424: an uncharged contract refusal sat
    // under a chip claiming a charge; a partial release sat under "Not sent").
    gloss: "Needs your review",
    tone: "red",
    icon: "report_problem",
  }),
});

/** A batch is editable while it is still collecting or awaiting release. */
export const isEditable = (status) => status === "open" || status === "holding";

/**
 * "Needs you" is Holding plus Failed plus Blocked: the states where the
 * distributor, and only the distributor, can move the batch forward.
 *
 * Blocked belongs here most of all -- nothing automatic will ever resolve it,
 * and money is already collected. Note it is deliberately NOT in
 * `isEditable`: the orders in a blocked batch have been charged, so editing
 * one would change a total somebody has already paid.
 */
export const needsYou = (status) =>
  status === "holding" ||
  status === "failed" ||
  status === "release_blocked";

/**
 * @param {string} status the backend batch state
 * @param {{moneyTaken?: boolean|null, partial?: {notSent: number, total: number, charged?: number}|null}} [facts]
 *   for `release_blocked` only:
 *   - moneyTaken: true when a stranded order was charged, false when stranded
 *     orders are known to be uncharged, null/absent when that is not known yet
 *     (the gloss then makes no claim about money either way). It is a fact
 *     about the STRANDED orders, not the batch.
 *   - partial: set when at least one order WAS sent. The gloss is then scoped
 *     to counts ("2 of 3 orders not sent") and makes NO money claim, because
 *     "nothing charged" / "not sent" are false of a batch that is partly sent
 *     (#424 retest: one order charged $27.12 and sent under a chip saying
 *     "Not sent, nothing charged").
 */
export const statusMeta = (status, facts = {}) => {
  const meta = BATCH_STATUS[status] || BATCH_STATUS.open;
  if (status !== "release_blocked") return meta;
  if (facts.partial) {
    return {
      ...meta,
      gloss:
        `${facts.partial.notSent} of ${facts.partial.total} orders not sent` +
        (typeof facts.partial.charged === "number"
          ? `, ${facts.partial.charged} charged`
          : ""),
    };
  }
  if (facts.moneyTaken === true) return { ...meta, gloss: "Charged, not sent" };
  if (facts.moneyTaken === false) return { ...meta, gloss: "Not sent, nothing charged" };
  return meta;
};

/**
 * ONE definition of "how many orders were charged" for every screen that says
 * it (row chip, row banner, release result), so they cannot disagree (#424).
 *
 * Charged = every SENT order (a sent order is always charged, whatever its
 * payment_state field says) UNION every not-sent order that holds money (its
 * payment state is `charged`, or its stranded item has chargedCents > 0), as a
 * set of order ids so one order counts once.
 *
 * `known` is false when some not-sent order has no affirmative outcome: no
 * loaded stranded item for it and a payment state that is absent (defaulted)
 * or not a known uncharged state. The count is then not trustworthy and
 * callers must say nothing about money (never "0 charged"): money claims are
 * affirmative-only.
 *
 * @param {Array} orders live (non-canceled) orders: {rawId, paymentState, paymentStateKnown}
 * @param {Array<number|string>} deliveredIds order ids whose release item is done
 * @param {Array} items stranded items: {orderId, chargedCents}
 * @returns {{count: number, known: boolean}}
 */
export const chargeFacts = (orders, deliveredIds, items) => {
  const delivered = new Set((deliveredIds || []).map(Number));
  const itemByOrder = new Map((items || []).map((i) => [Number(i.orderId), i]));
  const UNCHARGED = new Set(["charge_failed", "pending_capture"]);
  const charged = new Set();
  let known = true;
  for (const o of orders || []) {
    const id = Number(o.rawId);
    const item = itemByOrder.get(id);
    if (delivered.has(id) || o.paymentState === "charged" || (item && item.chargedCents > 0)) {
      charged.add(id);
    } else if (item) {
      // A loaded per-order outcome with no money on it.
    } else if (!(o.paymentStateKnown && UNCHARGED.has(o.paymentState))) {
      known = false;
    }
  }
  return { count: charged.size, known };
};

/** The sentence about money for a partial release; "" when it cannot be said. */
export const chargedSentence = ({ count, known }) => {
  if (!known) return "Check each order's payment status before you retry.";
  if (count === 0) return "No orders were charged.";
  return `${count} ${count === 1 ? "order was" : "orders were"} charged.`;
};
